import {
  collection,
  getDocs,
  limit,
  query,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { AppUser, MediaHistoryItem } from '../types';
import { buildSearchIndex, canQuerySearch, MIN_SEARCH_QUERY_LENGTH, normalizeSearchInput } from './searchIndex';
import { fallbackUsername } from '../utils/username';

export interface SearchResult {
  id: string;
  type: 'channel' | 'short' | 'post' | 'long' | 'live';
  title: string;
  subtitle: string;
  imageUrl?: string;
  user?: AppUser;
  media?: MediaHistoryItem;
}

export const searchUsers = async (searchTerm: string): Promise<AppUser[]> => {
  const normalized = normalizeSearchInput(searchTerm);
  if (!canQuerySearch(normalized)) return [];

  const tokens = buildSearchIndex(normalized).slice(0, 10);
  if (!tokens.length) return [];

  try {
    const snapshot = await getDocs(
      query(collection(db, 'publicProfiles'), where('searchKeywords', 'array-contains-any', tokens), limit(20))
    );

    return snapshot.docs
      .map(docSnap => {
        const data = docSnap.data() as Partial<AppUser> & { photoURL?: string; searchKeywords?: string[] };
        return {
          ...data,
          uid: docSnap.id,
          photoURL: data.photoURL || '',
          displayName: data.displayName || 'Clue200 user',
          username: data.username || fallbackUsername(docSnap.id, data.email),
          email: data.email || '',
          createdAt: data.createdAt || new Date().toISOString()
        } as AppUser;
      })
      .filter(user => {
        const displayName = (user.displayName || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        const clueId = (user.clueId || '').toLowerCase();
        return displayName.includes(normalized) ||
               username.includes(normalized) ||
               clueId.includes(normalized) ||
               (user.searchKeywords || []).some(value => value === normalized);
      })
      .sort((a, b) => {
        const aPriority = `${a.displayName} ${a.username}`.toLowerCase().startsWith(normalized) ? 0 : 1;
        const bPriority = `${b.displayName} ${b.username}`.toLowerCase().startsWith(normalized) ? 0 : 1;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return `${a.displayName || ''} ${a.username || ''}`.localeCompare(`${b.displayName || ''} ${b.username || ''}`);
      })
      .slice(0, 20);
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

export const listCreators = async (): Promise<AppUser[]> => {
  try {
    const snapshot = await getDocs(query(
      collection(db, 'publicProfiles'),
      where('publishedContentCount', '>', 0),
      limit(100)
    ));
    return snapshot.docs.map(snapshotItem => {
      const data = snapshotItem.data() as Partial<AppUser>;
      return {
        ...data,
        uid: snapshotItem.id,
        email: '',
        displayName: data.displayName || 'Clue200 creator',
        username: data.username || fallbackUsername(snapshotItem.id, data.email),
        photoURL: data.photoURL || '',
        followerCount: data.followerCount || 0,
        followersCount: data.followerCount || 0,
        publishedContentCount: data.publishedContentCount || 0
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  } catch (error) {
    console.error('Error loading creator directory:', error);
    return [];
  }
};

const normalizeTerm = (term: string) => normalizeSearchInput(term);

export const searchContent = async (term: string): Promise<SearchResult[]> => {
  const normalized = normalizeTerm(term);
  if (!canQuerySearch(normalized)) return [];

  const tokens = buildSearchIndex(normalized).slice(0, 10);
  if (!tokens.length) return [];

  const [profiles, media] = await Promise.all([
    getDocs(query(collection(db, 'publicProfiles'), where('searchKeywords', 'array-contains-any', tokens), limit(10))),
    getDocs(query(collection(db, 'media'), where('searchKeywords', 'array-contains-any', tokens), limit(30)))
  ]);

  const results: SearchResult[] = profiles.docs.map(snapshot => {
    const data = snapshot.data() as Omit<AppUser, 'uid' | 'category'> & { searchKeywords?: string[] };
    const username = data.username || fallbackUsername(snapshot.id, data.email);
    return {
      id: snapshot.id,
      type: 'channel',
      title: data.displayName || username || 'YeTryKar channel',
      subtitle: `@${username}`,
      imageUrl: data.photoURL,
      user: { ...data, uid: snapshot.id, username }
    };
  });

  media.docs.forEach(snapshot => {
    const data = snapshot.data() as MediaHistoryItem & { type?: string; caption?: string; authorName?: string; searchKeywords?: string[] };
    const type = data.type === 'short' ? 'short' : data.type === 'long' ? 'long' : data.type === 'live' ? 'live' : 'post';
    results.push({
      id: snapshot.id,
      type,
      title: data.title || data.caption || 'Untitled content',
      subtitle: data.authorName || 'Clue200 creator',
      imageUrl: data.thumbnailUrl || data.mediaUrl,
      media: { ...data, id: snapshot.id, type: type === 'post' ? 'photo' : type }
    });
  });

  return results.filter(item => {
    const title = (item.title || '').toLowerCase();
    const subtitle = (item.subtitle || '').toLowerCase();
    return title.includes(normalized) || subtitle.includes(normalized);
  });
};

export const searchWithFilter = async (term: string, filter: 'all' | 'title' | 'username' | 'channel'): Promise<SearchResult[]> => {
  const normalized = normalizeTerm(term);
  if (!canQuerySearch(normalized)) return [];

  const tokens = buildSearchIndex(normalized).slice(0, 10);
  if (!tokens.length) return [];

  const results: SearchResult[] = [];

  if (filter === 'all' || filter === 'channel' || filter === 'username') {
    const profiles = await getDocs(query(collection(db, 'publicProfiles'), where('searchKeywords', 'array-contains-any', tokens), limit(20)));
    profiles.docs.forEach(snapshot => {
      const data = snapshot.data() as Omit<AppUser, 'uid' | 'category'> & { searchKeywords?: string[]; username?: string; displayName?: string; photoURL?: string };
      const username = data.username || fallbackUsername(snapshot.id, data.email);
      const uname = username.toLowerCase();
      const dName = (data.displayName || '').toLowerCase();
      if (filter === 'username' && !uname.includes(normalized)) return;
      if (filter === 'channel' && !dName.includes(normalized)) return;
      results.push({
        id: snapshot.id,
        type: 'channel',
        title: data.displayName || username || 'YeTryKar channel',
        subtitle: `@${username}`,
        imageUrl: data.photoURL,
        user: { ...data, uid: snapshot.id, username } as AppUser
      });
    });
  }

  if (filter === 'all' || filter === 'title') {
    const media = await getDocs(query(collection(db, 'media'), where('searchKeywords', 'array-contains-any', tokens), limit(30)));
    media.docs.forEach(snapshot => {
      const data = snapshot.data() as Omit<MediaHistoryItem, 'type'> & { type?: string; caption?: string; authorName?: string; searchKeywords?: string[]; title?: string; thumbnailUrl?: string; mediaUrl?: string };
      const title = (data.title || data.caption || '').toLowerCase();
      if (filter === 'title' && !title.includes(normalized)) return;
      const type = data.type === 'short' || data.type === 'minis' ? 'short' : data.type === 'long' ? 'long' : data.type === 'live' ? 'live' : 'post';
      results.push({
        id: snapshot.id,
        type,
        title: data.title || data.caption || 'Untitled content',
        subtitle: data.authorName || 'Clue200 creator',
        imageUrl: data.thumbnailUrl || data.mediaUrl,
        media: { ...data, id: snapshot.id, type: type === 'post' ? 'photo' : type }
      });
    });
  }

  return results;
};
